/**
 * Product Dimensions Management
 * ==============================
 * Input and manage SKU physical dimensions for Blended Metric calculations
 * Supports Excel upload and manual entry
 */

import { useState, useEffect } from 'react';
import GlobalHeader from '../components/GlobalHeader';
import { Package, Upload, Plus, Trash2, Save, Download, AlertCircle, CheckCircle, Ruler } from 'lucide-react';

interface ProductDimension {
  id?: string;
  upc: string;
  sku_name: string;
  width_inches: number;
  height_inches: number;
  depth_inches: number;
  shelf_facings: number;
  total_linear_inches?: number;
  category?: string;
  notes?: string;
}

export default function ProductDimensions() {
  const [dimensions, setDimensions] = useState<ProductDimension[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [newRow, setNewRow] = useState<Partial<ProductDimension>>({
    shelf_facings: 1
  });
  const [uploadStatus, setUploadStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  useEffect(() => {
    loadDimensions();
  }, []);

  const loadDimensions = async () => {
    setIsLoading(true);
    setUploadStatus({ type: null, message: '' });
    
    try {
      const response = await fetch('/api/product-dimensions');
      console.log('[ProductDimensions] GET response status:', response.status);
      
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        console.log('[ProductDimensions] GET response data:', data);
        
        if (data.success) {
          setDimensions(data.data || []);
          console.log('[ProductDimensions] Loaded', data.data?.length || 0, 'dimensions');
        } else {
          console.error('[ProductDimensions] API returned success=false:', data);
          setUploadStatus({
            type: 'error',
            message: data.error || 'Failed to load dimensions'
          });
        }
      } else {
        const text = await response.text();
        console.error('[ProductDimensions] Non-JSON response:', text.substring(0, 200));
        setUploadStatus({
          type: 'error',
          message: 'Server error - received HTML instead of JSON'
        });
      }
    } catch (error: any) {
      console.error('Failed to load dimensions:', error);
      setUploadStatus({
        type: 'error',
        message: error.message || 'Failed to connect to server'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadStatus({ type: null, message: '' });

    try {
      // Read file as text
      const text = await file.text();
      
      // Parse CSV
      const lines = text.split('\n').filter(line => line.trim());
      if (lines.length < 2) {
        throw new Error('File appears empty or invalid');
      }

      // Parse header row
      const headers = lines[0].split(',').map(h => h.trim());
      
      // Find column indices (support variations)
      const getColIndex = (names: string[]) => {
        for (const name of names) {
          const idx = headers.findIndex(h => h.toLowerCase().includes(name.toLowerCase()));
          if (idx !== -1) return idx;
        }
        return -1;
      };

      const upcIdx = getColIndex(['upc', 'sku']);
      const nameIdx = getColIndex(['name', 'product', 'description']);
      const widthIdx = getColIndex(['width']);
      const heightIdx = getColIndex(['height']);
      const depthIdx = getColIndex(['depth']);
      const facingsIdx = getColIndex(['facings', 'facing']);
      const categoryIdx = getColIndex(['category']);

      if (upcIdx === -1 || widthIdx === -1 || heightIdx === -1) {
        throw new Error('Required columns not found: UPC, Width, Height');
      }

      // Parse data rows
      const dimensions = [];
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',').map(c => c.trim());
        
        if (cols.length < 3) continue; // Skip empty rows
        
        const dim: any = {
          upc: cols[upcIdx],
          sku_name: nameIdx !== -1 ? cols[nameIdx] : `SKU ${cols[upcIdx]}`,
          width_inches: parseFloat(cols[widthIdx]),
          height_inches: parseFloat(cols[heightIdx]),
          depth_inches: depthIdx !== -1 ? parseFloat(cols[depthIdx]) : null,
          shelf_facings: facingsIdx !== -1 ? parseInt(cols[facingsIdx]) : 1,
          category: categoryIdx !== -1 ? cols[categoryIdx] : null
        };

        if (dim.upc && !isNaN(dim.width_inches) && !isNaN(dim.height_inches)) {
          dimensions.push(dim);
        }
      }

      if (dimensions.length === 0) {
        throw new Error('No valid product dimensions found in file');
      }

      // Send to API
      const response = await fetch('/api/product-dimensions/upload', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ dimensions })
      });

      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        throw new Error('Server returned non-JSON response');
      }

      if (data.success) {
        setUploadStatus({
          type: 'success',
          message: `Successfully uploaded ${data.count} product dimensions!`
        });
        loadDimensions();
      } else {
        setUploadStatus({
          type: 'error',
          message: data.error || 'Upload failed'
        });
      }
    } catch (error: any) {
      setUploadStatus({
        type: 'error',
        message: error.message || 'Upload failed'
      });
    }

    // Reset file input
    event.target.value = '';
  };

  const addNewRow = async () => {
    if (!newRow.upc || !newRow.sku_name || !newRow.width_inches || !newRow.height_inches) {
      setUploadStatus({
        type: 'error',
        message: 'Please fill in required fields: UPC, Name, Width, and Height'
      });
      return;
    }

    setIsSaving(true);
    setUploadStatus({ type: null, message: '' });
    
    try {
      console.log('Adding product dimension:', newRow);
      
      const response = await fetch('/api/product-dimensions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newRow)
      });

      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        console.error('Non-JSON response:', text.substring(0, 200));
        throw new Error('Server returned non-JSON response');
      }

      if (data.success) {
        setUploadStatus({
          type: 'success',
          message: 'Product dimension added successfully!'
        });
        setNewRow({ shelf_facings: 1 });
        loadDimensions();
      } else {
        setUploadStatus({
          type: 'error',
          message: data.error || 'Failed to add product'
        });
      }
    } catch (error: any) {
      console.error('Failed to add dimension:', error);
      setUploadStatus({
        type: 'error',
        message: error.message || 'Failed to add product'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const deleteRow = async (id: string) => {
    if (!confirm('Delete this product dimension?')) return;

    try {
      await fetch(`/api/product-dimensions?id=${id}`, { method: 'DELETE' });
      loadDimensions();
    } catch (error) {
      console.error('Failed to delete:', error);
    }
  };

  const downloadTemplate = () => {
    const csv = [
      ['UPC', 'SKU_Name', 'Width_Inches', 'Height_Inches', 'Depth_Inches', 'Shelf_Facings', 'Category', 'Notes'],
      ['2585285', 'VITAMIN C SERUM 1.0OZ', '2.5', '5.0', '1.5', '2', 'Skincare', 'Example product'],
      ['2585288', 'CLEANSER 3.4OZ', '3.0', '6.0', '2.0', '1', 'Skincare', '']
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'product_dimensions_template.csv';
    a.click();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <GlobalHeader 
        title="Product Dimensions"
        subtitle="Manage SKU measurements for Shelf Performance analysis"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg">
                <Ruler className="w-7 h-7 text-white" />
              </div>
              <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                Product Dimensions
              </h1>
              <p className="text-gray-600 mt-1">Input SKU measurements for PatternIQ Shelf Performance Intelligence</p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={downloadTemplate}
                className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-300 hover:border-gray-400 text-gray-700 rounded-lg font-semibold shadow-sm transition-all"
              >
                <Download className="w-5 h-5" />
                Download Template
              </button>
              
              <label className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg font-semibold shadow-md cursor-pointer transition-all">
                <Upload className="w-5 h-5" />
                Upload Excel/CSV
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
            </div>
          </div>
        </div>

        {/* Upload Status */}
        {uploadStatus.type && (
          <div className={`mb-6 p-4 rounded-xl border-2 ${
            uploadStatus.type === 'success'
              ? 'bg-green-50 border-green-300'
              : 'bg-red-50 border-red-300'
          }`}>
            <div className="flex items-center gap-3">
              {uploadStatus.type === 'success' ? (
                <CheckCircle className="w-6 h-6 text-green-600" />
              ) : (
                <AlertCircle className="w-6 h-6 text-red-600" />
              )}
              <p className={`font-semibold ${
                uploadStatus.type === 'success' ? 'text-green-900' : 'text-red-900'
              }`}>
                {uploadStatus.message}
              </p>
            </div>
          </div>
        )}

        {/* Info Banner */}
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-6 mb-6">
          <div className="flex items-start gap-3">
            <Package className="w-5 h-5 text-purple-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-purple-900 mb-2">About Space Productivity Score</h3>
              <p className="text-sm text-purple-700 mb-3">
                PatternIQ's <strong>"Space Productivity Score"</strong> evaluates how efficiently each inch of shelf space generates revenue. Industry benchmark: <strong>$2.64 per inch</strong>
              </p>
              <div className="text-sm text-purple-700">
                <strong>Proprietary Formula</strong>: ($/inch × 40%) + (units/inch × 15%) + (margin/inch × 45%)
                <br />
                <strong>Required</strong>: Product width in inches and shelf facings to calculate linear shelf space
              </div>
            </div>
          </div>
        </div>

        {/* Add New Product */}
        <div className="bg-white rounded-xl border-2 border-gray-200 shadow-lg p-6 mb-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Plus className="w-5 h-5" />
            Add Product Dimension
          </h3>
          <div className="grid grid-cols-8 gap-3">
            <input
              type="text"
              placeholder="UPC *"
              value={newRow.upc || ''}
              onChange={(e) => setNewRow({ ...newRow, upc: e.target.value })}
              className="col-span-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <input
              type="text"
              placeholder="Product Name *"
              value={newRow.sku_name || ''}
              onChange={(e) => setNewRow({ ...newRow, sku_name: e.target.value })}
              className="col-span-2 px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <input
              type="number"
              step="0.1"
              placeholder="Width (in) *"
              value={newRow.width_inches || ''}
              onChange={(e) => setNewRow({ ...newRow, width_inches: parseFloat(e.target.value) })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <input
              type="number"
              step="0.1"
              placeholder="Height (in) *"
              value={newRow.height_inches || ''}
              onChange={(e) => setNewRow({ ...newRow, height_inches: parseFloat(e.target.value) })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <input
              type="number"
              step="0.1"
              placeholder="Depth (in)"
              value={newRow.depth_inches || ''}
              onChange={(e) => setNewRow({ ...newRow, depth_inches: parseFloat(e.target.value) })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <input
              type="number"
              placeholder="Facings"
              value={newRow.shelf_facings || 1}
              onChange={(e) => setNewRow({ ...newRow, shelf_facings: parseInt(e.target.value) })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <button
              onClick={addNewRow}
              disabled={isSaving}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors disabled:bg-gray-400"
            >
              {isSaving ? 'Saving...' : <><Save className="w-4 h-4" /> Add</>}
            </button>
          </div>
        </div>

        {/* Dimensions Table */}
        <div className="bg-white rounded-xl border-2 border-gray-200 shadow-lg overflow-hidden">
          <div className="p-4 bg-gray-50 border-b border-gray-200">
            <h3 className="text-lg font-bold text-gray-900">
              Product Dimensions ({dimensions.length} products)
            </h3>
          </div>
          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase bg-gray-50">UPC</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase bg-gray-50">Product Name</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase bg-gray-50">Width (in)</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase bg-gray-50">Height (in)</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase bg-gray-50">Depth (in)</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase bg-gray-50">Facings</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase bg-gray-50">Linear Inches</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase bg-gray-50">Category</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase bg-gray-50">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {dimensions.length === 0 && !isLoading ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-12 text-center">
                      <Package className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600 mb-2">No product dimensions yet</p>
                      <p className="text-sm text-gray-500">Add products manually or upload an Excel/CSV file</p>
                    </td>
                  </tr>
                ) : (
                  dimensions.map((dim) => (
                    <tr key={dim.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-mono text-gray-900">{dim.upc}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{dim.sku_name}</td>
                      <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">{dim.width_inches}</td>
                      <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">{dim.height_inches}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-600">{dim.depth_inches || '-'}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900">{dim.shelf_facings}</td>
                      <td className="px-4 py-3 text-sm text-right font-bold text-blue-600">
                        {dim.total_linear_inches ? parseFloat(dim.total_linear_inches).toFixed(1) : 
                         (dim.width_inches && dim.shelf_facings ? (dim.width_inches * dim.shelf_facings).toFixed(1) : '-')}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{dim.category || '-'}</td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => deleteRow(dim.id!)}
                          className="text-red-600 hover:text-red-800 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Helper Text */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-blue-900 mb-2">💡 Tips for Measuring Products</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li><strong>Width:</strong> Measure the front-facing width (what you see on shelf)</li>
            <li><strong>Height:</strong> Vertical dimension of the product</li>
            <li><strong>Depth:</strong> How deep it sits on shelf (optional)</li>
            <li><strong>Shelf Facings:</strong> How many units displayed side-by-side (typically 1-4)</li>
            <li><strong>Linear Inches:</strong> Width × Facings = total shelf space occupied</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

